import classNames from "classnames";
import moment from "moment";
import React, { FC, useEffect, useRef, useState } from "react";
import { LuDownload, LuPaperclip, LuPlus } from "react-icons/lu";
import { toast } from "react-toastify";

import { ContactFileModel } from "../../../../../../factories/contacts/models/contact-file.model";
import { useLazyGetPreviewWorkFileQuery } from "../../../../../../slices/FilesSlice";
import { downloadFileByUrl, formatFileSize } from "../../../files/WorkFiles";
import { fileTypeLabel } from "./workNotesChat.utils";

const FileBadge: FC<{ name: string; onPurple?: boolean }> = ({
  name,
  onPurple,
}) => (
  <div
    className={classNames(
      "flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[9px] font-bold",
      onPurple ? "bg-white text-[#6B4EFF]" : "bg-[#F1EEFF] text-[#6B4EFF]",
    )}>
    {fileTypeLabel(name)}
  </div>
);

export const downloadWorkFile = async (
  getUrl: ReturnType<typeof useLazyGetPreviewWorkFileQuery>[0],
  fileId: string,
  fileName: string,
) => {
  try {
    const url = (await getUrl(fileId).unwrap())?.documentUrl;
    if (!url) throw new Error("No link");
    await downloadFileByUrl(url, fileName);
  } catch {
    toast("This file couldn't be downloaded.", { type: "error" });
  }
};

/** A file shared in the notes chat; click opens it, the arrow downloads it. */
export const FileNoteCard: FC<{
  name: string;
  file?: ContactFileModel;
  mine: boolean;
  onOpen: () => void;
  onDownload: () => void;
}> = ({ name, file, mine, onOpen, onDownload }) => (
  <div
    className={classNames(
      "flex w-[230px] max-w-full items-center gap-2.5 rounded-xl p-2",
      mine ? "bg-white/15" : "bg-white",
      !file && "opacity-70",
    )}>
    <button
      type={"button"}
      disabled={!file}
      onClick={onOpen}
      title={file ? `Open ${name}` : "This file was deleted from Files"}
      className={"flex min-w-0 flex-1 items-center gap-2.5 text-left"}>
      <FileBadge name={name} onPurple={mine} />
      <span className={"min-w-0"}>
        <span className={"block truncate text-[13px] font-semibold"}>
          {name}
        </span>
        <span
          className={classNames(
            "block text-[11px]",
            mine ? "text-white/80" : "text-gray-500",
          )}>
          {file
            ? `${fileTypeLabel(name)} · ${formatFileSize(file.documentSize)}`
            : "Deleted from Files"}
        </span>
      </span>
    </button>
    {file && (
      <button
        type={"button"}
        aria-label={`Download ${name}`}
        onClick={onDownload}
        className={classNames(
          "flex h-7 w-7 shrink-0 items-center justify-center rounded-md",
          mine ? "hover:bg-white/20" : "text-gray-500 hover:bg-gray-100",
        )}>
        <LuDownload size={15} />
      </button>
    )}
  </div>
);

/** One row of the header's file list (any kind of file of the work item). */
export type NoteMenuFile = { id: string; name: string; date: string | null };

/**
 * Paperclip counter in the Notes header: lists ALL the work item's files, the same list as its
 * Files tab (QA batch 14, NOTE-016: it counted only older work files).
 */
export const NoteFilesMenu = <T extends NoteMenuFile>({
  files,
  onOpen,
  onUpload,
  onViewAll,
}: {
  files: T[];
  onOpen: (file: T) => void;
  onUpload: () => void;
  onViewAll: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const newestFirst = [...files].sort(
    (a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime(),
  );

  return (
    // The list is placed against the Notes header (positioned parent), so it
    // spans the panel instead of overflowing a narrow one.
    <div ref={ref}>
      <button
        type={"button"}
        aria-label={`Files (${files.length})`}
        title={"Files on this work item"}
        onClick={() => setOpen((value) => !value)}
        className={classNames(
          "flex h-8 items-center gap-1 rounded-lg border px-2 text-[13px] font-semibold text-[#1F2433]",
          open
            ? "border-[#CFC6FF] bg-[#F1EEFF]"
            : "border-[#DCE2EA] hover:bg-[#F4F2FE]",
        )}>
        <LuPaperclip size={14} />
        {files.length}
      </button>
      {open && (
        <div
          className={
            "absolute left-3 right-3 top-[54px] z-30 overflow-hidden rounded-xl border border-[#EDEFF3] bg-white shadow-[0_12px_32px_rgba(31,36,51,0.14)]"
          }>
          <div
            className={
              "flex items-center justify-between border-b border-[#EDEFF3] px-3.5 py-3"
            }>
            <span className={"text-[13px] font-bold text-[#1F2433]"}>
              Files
            </span>
            <span className={"text-xs text-gray-500"}>
              {files.length} total
            </span>
          </div>
          <div className={"max-h-[240px] overflow-y-auto"}>
            {newestFirst.length ? (
              newestFirst.map((file) => (
                <button
                  key={file.id}
                  type={"button"}
                  onClick={() => {
                    setOpen(false);
                    onOpen(file);
                  }}
                  className={
                    "flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left hover:bg-[#F7F8FA]"
                  }>
                  <FileBadge name={file.name} />
                  <span className={"min-w-0"}>
                    <span
                      className={
                        "block truncate text-[13px] font-semibold text-[#1F2433]"
                      }>
                      {file.name}
                    </span>
                    {file.date && (
                      <span className={"block text-[11px] text-gray-500"}>
                        {moment(file.date).format("MMM D, YYYY")}
                      </span>
                    )}
                  </span>
                </button>
              ))
            ) : (
              <div className={"px-3.5 py-4 text-center text-xs text-gray-500"}>
                No files yet
              </div>
            )}
          </div>
          <div className={"grid grid-cols-2 border-t border-[#EDEFF3]"}>
            <button
              type={"button"}
              onClick={() => {
                setOpen(false);
                onUpload();
              }}
              className={
                "flex items-center justify-center gap-1.5 py-2.5 text-[13px] font-semibold text-[#6B4EFF] hover:bg-[#F7F8FA]"
              }>
              <LuPlus size={14} /> Upload
            </button>
            <button
              type={"button"}
              onClick={() => {
                setOpen(false);
                onViewAll();
              }}
              className={
                "border-l border-[#EDEFF3] py-2.5 text-[13px] font-semibold text-[#1F2433] hover:bg-[#F7F8FA]"
              }>
              View all
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
